import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Calendar, Lock, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MyPublications() {
  const [publications, setPublications] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadPublications();
    loadMembership();
  }, []);

  const loadPublications = async () => {
    try {
      const res = await axios.get(`${API}/my-publications`, { withCredentials: true });
      setPublications(res.data);
    } catch (error) {
      console.error('Failed to load publications:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMembership = async () => {
    try {
      // Try to get membership status from My Learning endpoint or create a simple check
      const res = await axios.get(`${API}/student/dashboard`, { withCredentials: true });
      // For now, we'll check if user has publications
      setMembership(null); // Will be set if subscription exists
    } catch (error) {
      console.error('Failed to load membership:', error);
    }
  };

  const groupByCategory = () => {
    const grouped = {};
    publications.forEach(pub => {
      const category = pub.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(pub);
    });
    return grouped;
  };

  const groupedPublications = groupByCategory();

  return (
    <Layout>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B5A3C] to-[#6B4423] text-white py-16 mb-12">
        <div className="container mx-auto px-6 text-center">
          <BookOpen className="w-20 h-20 mx-auto mb-6" />
          <h1 className="text-6xl font-bold retro-title mb-4">MY PUBLICATIONS</h1>
          <p className="text-xl">Your personal library of purchased and subscribed publications</p>
        </div>
      </div>

      {/* Back to Publication Center */}
      <div className="container mx-auto px-6 mb-8">
        <button
          onClick={() => navigate('/publication-center')}
          className="bg-gradient-to-r from-[#8B5A3C] to-[#6B4423] text-white px-6 py-3 rounded-lg font-bold hover:shadow-xl transition-all"
        >
          ← Back to Publication Center
        </button>
      </div>

      {/* Subscription Status */}
      {membership && (
        <div className="container mx-auto px-6 mb-8">
          <div className="bg-green-50 border-4 border-green-500 p-6 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
              <div>
                <h3 className="text-xl font-bold text-green-800">Active Subscription</h3>
                <p className="text-gray-700">You have unlimited access to all publications</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Publications List */}
      <div className="container mx-auto px-6 pb-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#8B5A3C] mx-auto"></div>
          </div>
        ) : publications.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-20 h-20 text-gray-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-600 mb-4">No Publications Yet</h2>
            <p className="text-xl text-gray-500 mb-8">
              Start building your library by purchasing publications or subscribing for unlimited access
            </p>
            <button
              onClick={() => navigate('/publication-center')}
              className="bg-gradient-to-r from-[#8B5A3C] to-[#6B4423] text-white px-8 py-4 rounded-lg font-bold text-xl hover:shadow-xl transition-all"
            >
              Browse Publications
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedPublications).map(([category, pubs]) => (
              <div key={category}>
                <h2 className="text-3xl font-bold text-[#6B4423] mb-6 capitalize retro-title">
                  {category}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {pubs.map(pub => (
                    <div
                      key={pub.id}
                      className="bg-white rounded-lg shadow-lg border-4 border-gray-200 hover:border-[#8B5A3C] transition-all overflow-hidden group cursor-pointer"
                      onClick={() => navigate(`/publications/${pub.id}/read`)}
                    >
                      {pub.cover_image_url && (
                        <div className="relative overflow-hidden">
                          <img
                            src={pub.cover_image_url}
                            alt={pub.title}
                            className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity"></div>
                        </div>
                      )}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-[#8B5A3C] transition-colors">
                          {pub.title}
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">By {pub.author}</p>
                        <p className="text-gray-700 mb-4 line-clamp-2">{pub.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(pub.published_at).toLocaleDateString()}</span>
                          </div>
                          {pub.pricing_type === 'free' || pub.free_trial ? (
                            <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                              FREE
                            </span>
                          ) : (
                            <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                              PURCHASED
                            </span>
                          )}
                        </div>
                        <button className="w-full mt-4 bg-gradient-to-r from-[#8B5A3C] to-[#6B4423] text-white px-4 py-3 rounded-lg font-bold hover:shadow-xl transition-all">
                          Read Now
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
